import React, { useState, useEffect, useMemo } from 'react';
import { Building2, HeartPulse, Link2, Users, Star, Monitor } from 'lucide-react';
import {
  VS_DOMAINS, VS_STREAMS_BY_DOMAIN, SIZE_CONTEXTS, PAIN_OPTIONS,
  painsFor, quadrantFor, QUADRANT_COLOR, LEVEL,
} from './data';

const DOMAIN_ICONS = { Building2, HeartPulse, Link2, Users, Star, Monitor };

const roiColor = { high: '#34d399', medium: '#fbbf24', low: '#f472b6' };
const effortColor = { low: '#34d399', medium: '#fbbf24', high: '#f472b6' };
const typeLabel = { automate: 'AUTOMATE', augment: 'AUGMENT', optimize: 'OPTIMIZE', accelerate: 'ACCELERATE', reimagine: 'REIMAGINE' };
const typeColor = { automate: '#818cf8', augment: '#38bdf8', optimize: '#34d399', accelerate: '#fbbf24', reimagine: '#e879f9' };

const HOURS_PER_FTE = 1800;
const DEFAULT_COST = 95000;
const DEFAULT_ADOPTION = 50;

// ─── URL state (shareable links) ──────────────────────────────────────────────
// ?vsd=<domain>&vss=<stream>&vsz=<size>&vsc=<company>&vsi=<industry>&vsp=<pain idx,...>&vsf=<FTEs>&vsk=<cost>&vsa=<adoption %>&vsb=<budget>
export const VS_URL_KEYS = ['vsd', 'vss', 'vsz', 'vsc', 'vsi', 'vsp', 'vsf', 'vsk', 'vsa', 'vsb'];

export function hasValueStreamUrlState() {
  try { return new URLSearchParams(window.location.search).has('vsd'); } catch (_) { return false; }
}

function readUrlState() {
  const empty = { step: 0, domain: null, stream: null, size: null, company: '', industry: '', pains: [], fte: '', cost: '', adoption: '', budget: '' };
  let q;
  try { q = new URLSearchParams(window.location.search); } catch (_) { return empty; }
  const domain = VS_DOMAINS.some(d => d.id === q.get('vsd')) ? q.get('vsd') : null;
  if (!domain) return empty;
  const stream = (VS_STREAMS_BY_DOMAIN[domain] || []).some(s => s.id === q.get('vss')) ? q.get('vss') : null;
  const size = SIZE_CONTEXTS[q.get('vsz')] ? q.get('vsz') : null;
  const num = (k) => { const v = q.get(k); return v && !isNaN(Number(v)) && Number(v) >= 0 ? v : ''; };
  return {
    step: !stream ? 1 : !size ? 2 : 3,
    domain, stream, size,
    company: (q.get('vsc') || '').slice(0, 80),
    industry: (q.get('vsi') || '').slice(0, 80),
    pains: (q.get('vsp') || '').split(',').map(Number).filter(i => Number.isInteger(i) && PAIN_OPTIONS[i]).map(i => PAIN_OPTIONS[i]),
    fte: num('vsf'), cost: num('vsk'), adoption: num('vsa'), budget: num('vsb'),
  };
}

function clearUrlState() {
  try {
    const url = new URL(window.location.href);
    VS_URL_KEYS.forEach(k => url.searchParams.delete(k));
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch (_) {}
}

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || !isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
};
const fmtNum = (v) => (v == null || !isFinite(v) ? '—' : Math.round(v).toLocaleString('en-US'));
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch (_) {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Small UI pieces ──────────────────────────────────────────────────────────
const sectionLabel = { fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#475569', fontFamily: 'Space Mono', marginBottom: 14 };
const fieldLabel = { fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 12, fontFamily: 'Space Mono', letterSpacing: '0.05em' };
const inputStyle = { width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 16px', color: '#f1f5f9', fontSize: 14, outline: 'none' };
const backBtn = { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, marginBottom: 20, padding: 0, display: 'flex', alignItems: 'center', gap: 6 };
const ghostBtn = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

function Pill({ color, children, solid }) {
  return (
    <span style={{ fontSize: 10, fontFamily: 'Space Mono', fontWeight: 700, padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap', background: solid ? color : `${color}18`, color: solid ? '#000' : color, border: solid ? 'none' : `1px solid ${color}40` }}>{children}</span>
  );
}

function Stat({ value, label, color = '#f1f5f9' }) {
  return (
    <div className="vsg-stat" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '12px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'Space Mono', marginTop: 4, letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, prefix, suffix, min = 0, max, step = 1, hint }) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 8, fontFamily: 'Space Mono', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ position: 'relative' }}>
        {prefix && <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 14 }}>{prefix}</span>}
        <input type="number" inputMode="decimal" value={value} min={min} max={max} step={step} placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, padding: '10px 12px', paddingLeft: prefix ? 26 : 12, paddingRight: suffix ? 30 : 12 }} />
        {suffix && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: 14 }}>{suffix}</span>}
      </div>
      {hint && <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>{hint}</div>}
    </label>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ValueStreamModal({ onClose }) {
  const [init] = useState(readUrlState);
  const [step, setStep] = useState(init.step); // 0=domain, 1=stream, 2=context, 3=output
  const [selectedDomain, setSelectedDomain] = useState(init.domain);
  const [selectedStream, setSelectedStream] = useState(init.stream);
  const [companySize, setCompanySize] = useState(init.size);
  const [painPoints, setPainPoints] = useState(init.pains);
  const [industry, setIndustry] = useState(init.industry);
  const [company, setCompany] = useState(init.company);
  const [fte, setFte] = useState(init.fte);
  const [cost, setCost] = useState(init.cost);
  const [adoption, setAdoption] = useState(init.adoption);
  const [budget, setBudget] = useState(init.budget);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [pinnedStep, setPinnedStep] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [toast, setToast] = useState('');

  const domainData = selectedDomain ? VS_DOMAINS.find(d => d.id === selectedDomain) : null;
  const streams = selectedDomain ? VS_STREAMS_BY_DOMAIN[selectedDomain] || [] : [];
  const streamData = selectedStream ? streams.find(s => s.id === selectedStream) : null;
  const sizeData = companySize ? SIZE_CONTEXTS[companySize] : null;

  const togglePain = (p) => setPainPoints(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ROI assumptions (blank inputs fall back to defaults for the chosen company size)
  const fteN = fte !== '' ? Math.max(0, Number(fte)) : (sizeData?.defaultFte ?? 10);
  const costN = cost !== '' ? Math.max(0, Number(cost)) : DEFAULT_COST;
  const adoptionN = Math.min(100, Math.max(0, adoption !== '' ? Number(adoption) : DEFAULT_ADOPTION)) / 100;
  const budgetN = budget !== '' && Number(budget) > 0 ? Number(budget) : null;

  // Scored, tailored opportunity list
  const analysis = useMemo(() => {
    if (!streamData) return null;
    const nSteps = streamData.steps.length;
    const effortWeight = sizeData?.effortWeight ?? 0.7;
    const items = streamData.steps.map((stepName, idx) => {
      const opp = streamData.opps[stepName];
      if (!opp) return { stepName, idx, opp: null };
      const pains = painsFor(opp);
      const matched = pains.filter(p => painPoints.includes(p));
      const share = 1 / nSteps; // assume team time is split evenly across steps
      const hours = fteN * HOURS_PER_FTE * share * opp.savings * adoptionN;
      const dollars = fteN * costN * share * opp.savings * adoptionN;
      const score = LEVEL[opp.roi] * 2 - LEVEL[opp.effort] * effortWeight + matched.length * 0.75 + opp.savings;
      return { stepName, idx, opp, pains, matched, hours, dollars, score, quadrant: quadrantFor(opp.roi, opp.effort) };
    });
    const opps = items.filter(x => x.opp);
    const ranked = [...opps].sort((a, b) => b.score - a.score || a.idx - b.idx);
    ranked.forEach((x, i) => { x.rank = i + 1; });
    const totalDollars = opps.reduce((s, x) => s + x.dollars, 0);
    const totalHours = opps.reduce((s, x) => s + x.hours, 0);
    return {
      items, opps, ranked, totalDollars, totalHours,
      highCount: opps.filter(x => x.opp.roi === 'high').length,
      quickWins: opps.filter(x => x.quadrant === 'Quick Win').length,
    };
  }, [streamData, sizeData, painPoints, fteN, costN, adoptionN]);

  const paybackMonths = analysis && budgetN && analysis.totalDollars > 0 ? budgetN / (analysis.totalDollars / 12) : null;

  // Shareable URL for the current map
  const shareUrl = useMemo(() => {
    if (!selectedDomain || !selectedStream || !companySize) return '';
    const q = new URLSearchParams();
    q.set('vsd', selectedDomain); q.set('vss', selectedStream); q.set('vsz', companySize);
    if (company.trim()) q.set('vsc', company.trim());
    if (industry.trim()) q.set('vsi', industry.trim());
    if (painPoints.length) q.set('vsp', painPoints.map(p => PAIN_OPTIONS.indexOf(p)).sort((a, b) => a - b).join(','));
    if (fte !== '') q.set('vsf', fte);
    if (cost !== '') q.set('vsk', cost);
    if (adoption !== '') q.set('vsa', adoption);
    if (budget !== '') q.set('vsb', budget);
    return `${window.location.origin}${window.location.pathname}?${q.toString()}`;
  }, [selectedDomain, selectedStream, companySize, company, industry, painPoints, fte, cost, adoption, budget]);

  // Keep the address bar in sync while viewing a map; clean it up on close
  useEffect(() => {
    if (step === 3 && shareUrl) {
      try { window.history.replaceState(null, '', shareUrl.replace(window.location.origin, '')); } catch (_) {}
    }
  }, [step, shareUrl]);
  useEffect(() => () => clearUrlState(), []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const buildMarkdown = () => {
    if (!analysis) return '';
    const title = `${streamData.name}: AI Value Stream Map${company.trim() ? ` for ${company.trim()}` : ''}`;
    const ctx = [domainData?.label, sizeData?.label, industry.trim()].filter(Boolean).join(' · ');
    const lines = [
      `# ${title}`, '', `_${ctx}_`, '',
      `**${analysis.opps.length} AI opportunities** · ${analysis.highCount} high-ROI · ${analysis.quickWins} quick wins`,
      `**Indicative annual savings:** ${fmtMoney(analysis.totalDollars)} (${fmtNum(analysis.totalHours)} hours freed)${paybackMonths ? ` · payback ≈ ${paybackMonths.toFixed(1)} months on ${fmtMoney(budgetN)}` : ''}`,
      painPoints.length ? `**Pain points:** ${painPoints.join(', ')}` : '', '',
      '## Process flow', '',
      analysis.items.map(x => x.opp ? `${x.stepName} [${typeLabel[x.opp.type]}]` : `${x.stepName} [no change]`).join(' → '), '',
      '## Prioritized AI opportunities', '',
      '| # | Opportunity | Step | Type | ROI | Effort | Priority | Est. savings/yr |',
      '|---|---|---|---|---|---|---|---|',
      ...analysis.ranked.map(x => `| ${x.rank} | ${x.opp.label} | ${x.stepName} | ${cap(x.opp.type)} | ${cap(x.opp.roi)} | ${cap(x.opp.effort)} | ${x.quadrant} | ${fmtMoney(x.dollars)} |`),
      '', '## Details', '',
      ...analysis.ranked.flatMap(x => [`**${x.rank}. ${x.opp.label}** (${x.stepName}): ${x.opp.impact}${x.matched.length ? ` _Addresses: ${x.matched.join(', ')}._` : ''}`, '']),
      '## Assumptions', '',
      `- ${fmtNum(fteN)} FTEs work in this value stream at ${fmtMoney(costN)} fully loaded cost each (${HOURS_PER_FTE.toLocaleString('en-US')} hours/year).`,
      `- Team time is split evenly across the ${streamData.steps.length} steps; ${Math.round(adoptionN * 100)}% of the potential savings is realized.`,
      '- Benchmarks are directional industry ranges, not a guarantee. Validate with your own baseline data.', '',
      shareUrl ? `[Open this map](${shareUrl})` : '',
      '', 'Generated with the AI Value Stream Generator at builditai.co',
    ];
    return lines.filter((l, i, arr) => !(l === '' && arr[i - 1] === '')).join('\n');
  };

  const onCopyLink = async () => flash((await copyText(shareUrl)) ? 'Link copied' : 'Copy failed, use the address bar');
  const onCopyMd = async () => flash((await copyText(buildMarkdown())) ? 'Markdown copied' : 'Copy failed');
  const onDownloadMd = () => {
    const slug = `${company.trim() || 'ai'}-${streamData.id}-value-stream`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadText(`${slug}.md`, buildMarkdown());
    flash('Downloaded');
  };
  const onPrint = () => window.print();

  const activeIdx = hoveredStep ?? pinnedStep ?? (analysis?.ranked[0]?.idx ?? null);
  const activeItem = analysis && activeIdx != null ? analysis.items[activeIdx] : null;
  const selectStep = (i) => setPinnedStep(prev => (prev === i ? null : i));

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
  const modal = { background: '#0d0d1a', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 24, width: '100%', maxWidth: 960, maxHeight: '90vh', overflowY: 'auto', padding: 0, position: 'relative' };

  const goToStep = (i) => { if (i < step) setStep(i); };

  return (
    <div className="vsg-overlay" style={overlay} onClick={onClose}>
      <div className="vsg-modal vsg-print-root" style={modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="AI Value Stream Generator">
        {/* Header */}
        <div className="vsg-head" style={{ padding: '32px 40px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#a78bfa', fontFamily: 'Space Mono', marginBottom: 6 }}>AI Strategy Tool</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9' }}>AI Value Stream Generator</h2>
          </div>
          <div className="vsg-noprint" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', gap: 6 }} aria-label={`Step ${step + 1} of 4`}>
              {['Domain', 'Stream', 'Context', 'Map'].map((label, i) => (
                <button key={label} title={label} onClick={() => goToStep(i)} aria-label={label}
                  style={{ width: 8, height: 8, padding: 0, border: 'none', borderRadius: '50%', background: i <= step ? '#a78bfa' : 'rgba(255,255,255,0.1)', transition: 'background 0.3s', cursor: i < step ? 'pointer' : 'default' }} />
              ))}
            </div>
            <button onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
          </div>
        </div>

        <div className="vsg-body" style={{ padding: '32px 40px' }}>

          {/* STEP 0: Domain Selection */}
          {step === 0 && (
            <div>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#f1f5f9', marginBottom: 8 }}>Which value stream domain?</h3>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 28 }}>Select the enterprise domain you want to analyze for AI optimization opportunities.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 14 }}>
                {VS_DOMAINS.map(d => {
                  const DIcon = DOMAIN_ICONS[d.icon];
                  const nStreams = (VS_STREAMS_BY_DOMAIN[d.id] || []).length;
                  return (
                    <button key={d.id} onClick={() => { if (d.id !== selectedDomain) setSelectedStream(null); setSelectedDomain(d.id); setStep(1); }}
                      style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${selectedDomain === d.id ? d.color : 'rgba(255,255,255,0.07)'}`, borderRadius: 16, padding: '20px 22px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    >
                      <div style={{ marginBottom: 10, color: d.color }}>{DIcon && <DIcon size={24} color={d.color} strokeWidth={1.5} />}</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9', marginBottom: 6 }}>{d.label}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        {d.examples.slice(0, 2).map(ex => (
                          <span key={ex} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: `${d.color}18`, color: d.color, fontFamily: 'Space Mono' }}>{ex}</span>
                        ))}
                        <span style={{ fontSize: 10, color: '#475569', fontFamily: 'Space Mono', marginLeft: 2 }}>{nStreams} flows</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 1: Stream Selection */}
          {step === 1 && (
            <div>
              <button onClick={() => setStep(0)} style={backBtn}>← Back</button>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#f1f5f9', marginBottom: 8 }}>Which process flow?</h3>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 28 }}>Select the specific value stream within {domainData?.label} to map.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {streams.map(s => (
                  <button key={s.id} onClick={() => { setSelectedStream(s.id); setPinnedStep(null); setStep(2); }}
                    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${selectedStream === s.id ? (domainData?.color || '#a78bfa') : 'rgba(255,255,255,0.07)'}`, borderRadius: 14, padding: '18px 22px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{s.name}</div>
                      <span style={{ fontSize: 10, color: '#34d399', fontFamily: 'Space Mono' }}>{Object.keys(s.opps).length} AI opportunities</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {s.steps.slice(0, 4).map(st => (
                        <span key={st} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: 'rgba(255,255,255,0.04)', color: '#64748b', fontFamily: 'Space Mono' }}>{st}</span>
                      ))}
                      {s.steps.length > 4 && <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: 'rgba(255,255,255,0.04)', color: '#475569', fontFamily: 'Space Mono' }}>+{s.steps.length - 4} more</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Company Context */}
          {step === 2 && (
            <div>
              <button onClick={() => setStep(1)} style={backBtn}>← Back</button>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: '#f1f5f9', marginBottom: 8 }}>Tell us about your organization</h3>
              <p style={{ color: '#64748b', fontSize: 14, marginBottom: 28 }}>Company size and pain points change how opportunities are ranked. Smaller teams favor low-effort quick wins; your pain points push matching opportunities up.</p>

              <div style={{ marginBottom: 28 }}>
                <div style={fieldLabel}>COMPANY SIZE</div>
                <div className="vsg-size-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
                  {Object.entries(SIZE_CONTEXTS).map(([key, val]) => (
                    <button key={key} onClick={() => setCompanySize(key)}
                      style={{ background: companySize === key ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${companySize === key ? '#a78bfa' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, padding: '14px 18px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: companySize === key ? '#a78bfa' : '#f1f5f9', marginBottom: 4 }}>{val.label}</div>
                      <div style={{ fontSize: 11, color: '#475569' }}>AI Maturity: {val.maturity}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="vsg-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 28 }}>
                <label style={{ display: 'block' }}>
                  <div style={fieldLabel}>COMPANY (OPTIONAL)</div>
                  <input value={company} maxLength={80} onChange={e => setCompany(e.target.value)} placeholder="e.g., Acme Corp" style={inputStyle} />
                </label>
                <label style={{ display: 'block' }}>
                  <div style={fieldLabel}>INDUSTRY (OPTIONAL)</div>
                  <input value={industry} maxLength={80} onChange={e => setIndustry(e.target.value)} placeholder="e.g., Pharmaceuticals, Retail Banking..." style={inputStyle} />
                </label>
              </div>

              <div style={{ marginBottom: 32 }}>
                <div style={fieldLabel}>CURRENT PAIN POINTS (SELECT ALL THAT APPLY)</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PAIN_OPTIONS.map(p => (
                    <button key={p} onClick={() => togglePain(p)} aria-pressed={painPoints.includes(p)}
                      style={{ fontSize: 12, padding: '7px 14px', borderRadius: 99, border: `1px solid ${painPoints.includes(p) ? '#a78bfa' : 'rgba(255,255,255,0.08)'}`, background: painPoints.includes(p) ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.03)', color: painPoints.includes(p) ? '#a78bfa' : '#64748b', cursor: 'pointer', transition: 'all 0.2s' }}>{p}</button>
                  ))}
                </div>
              </div>

              <button onClick={() => { setPinnedStep(null); setStep(3); }} disabled={!companySize}
                style={{ background: companySize ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'rgba(255,255,255,0.05)', color: companySize ? '#fff' : '#475569', border: 'none', padding: '14px 32px', borderRadius: 12, fontWeight: 600, fontSize: 15, cursor: companySize ? 'pointer' : 'not-allowed', width: '100%' }}>
                Generate AI Value Stream Map →
              </button>
            </div>
          )}

          {/* STEP 3: Value Stream Output */}
          {step === 3 && streamData && analysis && (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <button className="vsg-noprint" onClick={() => setStep(2)} style={{ ...backBtn, marginBottom: 0 }}>← Refine inputs</button>
                  <h3 style={{ fontSize: 20, fontWeight: 600, color: '#f1f5f9', marginTop: 8 }}>{streamData.name}{company.trim() ? ` · ${company.trim()}` : ''}</h3>
                  <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{domainData?.label} · {sizeData?.label}{industry.trim() ? ` · ${industry.trim()}` : ''}</p>
                </div>
                <div className="vsg-noprint vsg-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={onCopyLink} style={ghostBtn}>🔗 Copy link</button>
                  <button onClick={onCopyMd} style={ghostBtn}>Copy Markdown</button>
                  <button onClick={onDownloadMd} style={ghostBtn}>↓ .md</button>
                  <button onClick={onPrint} style={ghostBtn}>Print / PDF</button>
                </div>
              </div>

              {/* Summary stats */}
              <div className="vsg-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
                <Stat value={analysis.highCount} label="HIGH ROI AREAS" color="#34d399" />
                <Stat value={analysis.quickWins} label="QUICK WINS" color="#34d399" />
                <Stat value={fmtMoney(analysis.totalDollars)} label="EST. SAVINGS / YR" color="#a78bfa" />
                <Stat value={fmtNum(analysis.totalHours)} label="HOURS FREED / YR" />
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                {['automate', 'augment', 'optimize', 'accelerate'].map(type => (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: typeColor[type] }} />
                    <span style={{ fontSize: 11, color: '#475569', fontFamily: 'Space Mono' }}>{typeLabel[type]}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: 'rgba(255,255,255,0.08)' }} />
                  <span style={{ fontSize: 11, color: '#475569', fontFamily: 'Space Mono' }}>NO CHANGE</span>
                </div>
              </div>

              {/* Value Stream Flow */}
              <div style={{ marginBottom: 20 }}>
                <div style={sectionLabel}>PROCESS FLOW → TAP OR HOVER A STEP FOR DETAILS</div>
                <div className="vsg-flow" style={{ display: 'flex', overflowX: 'auto', paddingBottom: 8, paddingTop: 2 }}>
                  {analysis.items.map(({ stepName, opp, idx }) => {
                    const isActive = activeIdx === idx;
                    const stepColor = opp ? typeColor[opp.type] : 'rgba(255,255,255,0.08)';
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
                        <button
                          onMouseEnter={() => setHoveredStep(idx)}
                          onMouseLeave={() => setHoveredStep(null)}
                          onFocus={() => setHoveredStep(idx)}
                          onBlur={() => setHoveredStep(null)}
                          onClick={() => selectStep(idx)}
                          aria-pressed={pinnedStep === idx}
                          style={{ position: 'relative', width: 128, background: isActive ? (opp ? `${stepColor}20` : 'rgba(255,255,255,0.06)') : 'rgba(255,255,255,0.03)', border: `2px solid ${opp ? stepColor : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, padding: '14px 10px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: pinnedStep === idx ? `0 0 0 2px ${opp ? stepColor : '#475569'}55` : 'none' }}>
                          {opp && <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: stepColor, color: '#000', fontSize: 9, padding: '2px 8px', borderRadius: '0 0 6px 6px', fontFamily: 'Space Mono', fontWeight: 700, whiteSpace: 'nowrap' }}>{typeLabel[opp.type]}</div>}
                          <div style={{ fontSize: 12, fontWeight: 600, color: opp ? '#f1f5f9' : '#475569', lineHeight: 1.3, marginTop: opp ? 8 : 0, textAlign: 'center' }}>{stepName}</div>
                          {opp?.roi === 'high' && <div style={{ textAlign: 'center', marginTop: 6 }}><span style={{ fontSize: 10, color: '#34d399', fontFamily: 'Space Mono' }}>HIGH ROI ★</span></div>}
                        </button>
                        {idx < analysis.items.length - 1 && (
                          <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', padding: '0 6px', color: '#334155', fontSize: 16 }}>→</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Step detail */}
              {activeItem && (activeItem.opp ? (
                <div className="vsg-noprint" style={{ background: `${typeColor[activeItem.opp.type]}0d`, border: `1px solid ${typeColor[activeItem.opp.type]}30`, borderRadius: 12, padding: '18px 22px', marginBottom: 28 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <Pill color={typeColor[activeItem.opp.type]} solid>{typeLabel[activeItem.opp.type]}</Pill>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#f1f5f9' }}>{activeItem.opp.label}</span>
                    <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'Space Mono' }}>↳ {activeItem.stepName}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Pill color={roiColor[activeItem.opp.roi]}>ROI: {activeItem.opp.roi.toUpperCase()}</Pill>
                      <Pill color={effortColor[activeItem.opp.effort]}>EFFORT: {activeItem.opp.effort.toUpperCase()}</Pill>
                      <Pill color={QUADRANT_COLOR[activeItem.quadrant]}>{activeItem.quadrant.toUpperCase()}</Pill>
                    </span>
                  </div>
                  <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>{activeItem.opp.impact}</p>
                  <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', fontSize: 12, color: '#64748b', fontFamily: 'Space Mono' }}>
                    <span>Priority #{activeItem.rank}</span>
                    <span>≈ {fmtMoney(activeItem.dollars)}/yr · {fmtNum(activeItem.hours)} hrs</span>
                    {activeItem.matched.length > 0 && <span style={{ color: '#a78bfa' }}>Addresses: {activeItem.matched.join(', ')}</span>}
                  </div>
                </div>
              ) : (
                <div className="vsg-noprint" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: '16px 20px', marginBottom: 28 }}>
                  <p style={{ color: '#64748b', fontSize: 14 }}><strong style={{ color: '#94a3b8' }}>{activeItem.stepName}:</strong> no AI opportunity mapped for this step. Good candidate for process standardization first.</p>
                </div>
              ))}

              {/* Impact vs Effort matrix */}
              <div style={{ marginBottom: 28 }}>
                <div style={sectionLabel}>PRIORITIZATION: IMPACT VS. EFFORT</div>
                <div className="vsg-matrix" style={{ display: 'grid', gridTemplateColumns: '28px repeat(3, minmax(0,1fr))', gridTemplateRows: 'repeat(3, auto) 24px', gap: 6 }}>
                  {['high', 'medium', 'low'].map(roi => (
                    <React.Fragment key={roi}>
                      <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 10, color: '#475569', fontFamily: 'Space Mono', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{roi.toUpperCase()}</div>
                      {['low', 'medium', 'high'].map(effort => {
                        const q = quadrantFor(roi, effort);
                        const cellItems = analysis.opps.filter(x => x.opp.roi === roi && x.opp.effort === effort);
                        return (
                          <div key={effort} style={{ minHeight: 74, background: `${QUADRANT_COLOR[q]}0a`, border: `1px solid ${QUADRANT_COLOR[q]}26`, borderRadius: 10, padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <div style={{ fontSize: 9, color: QUADRANT_COLOR[q], fontFamily: 'Space Mono', letterSpacing: '0.08em', opacity: 0.85 }}>{q.toUpperCase()}</div>
                            {cellItems.map(x => (
                              <button key={x.idx} onClick={() => selectStep(x.idx)} title={`${x.opp.label} (${x.stepName})`}
                                style={{ textAlign: 'left', fontSize: 11, lineHeight: 1.3, color: '#e2e8f0', background: activeIdx === x.idx ? `${typeColor[x.opp.type]}30` : 'rgba(255,255,255,0.04)', border: `1px solid ${typeColor[x.opp.type]}55`, borderLeft: `3px solid ${typeColor[x.opp.type]}`, borderRadius: 6, padding: '4px 7px', cursor: 'pointer' }}>
                                <span style={{ color: '#64748b', fontFamily: 'Space Mono', marginRight: 4 }}>#{x.rank}</span>{x.opp.label}
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  <div />
                  {['LOW EFFORT', 'MEDIUM EFFORT', 'HIGH EFFORT'].map(l => (
                    <div key={l} style={{ fontSize: 10, color: '#475569', fontFamily: 'Space Mono', textAlign: 'center', alignSelf: 'center' }}>{l}</div>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>Vertical axis: business impact. Start top-left: quick wins fund the strategic bets.</div>
              </div>

              {/* ROI estimator */}
              <div className="vsg-roi" style={{ marginBottom: 28, background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.18)', borderRadius: 16, padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                  <div style={{ ...sectionLabel, marginBottom: 0, color: '#a78bfa' }}>ROI ESTIMATOR</div>
                  <div style={{ fontSize: 11, color: '#475569' }}>Indicative only. Adjust to your numbers.</div>
                </div>
                <div className="vsg-roi-grid vsg-noprint" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 16 }}>
                  <NumberField label="FTEs IN THIS FLOW" value={fte} onChange={setFte} placeholder={String(sizeData?.defaultFte ?? 10)} step={1} />
                  <NumberField label="COST / FTE / YR" value={cost} onChange={setCost} placeholder={String(DEFAULT_COST)} prefix="$" step={1000} />
                  <NumberField label="ADOPTION" value={adoption} onChange={setAdoption} placeholder={String(DEFAULT_ADOPTION)} suffix="%" max={100} step={5} />
                  <NumberField label="AI BUDGET (OPT.)" value={budget} onChange={setBudget} placeholder="e.g. 250000" prefix="$" step={10000} />
                </div>
                <div className="vsg-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                  <Stat value={fmtMoney(analysis.totalDollars)} label="ANNUAL SAVINGS" color="#34d399" />
                  <Stat value={fmtNum(analysis.totalHours)} label="HOURS FREED" />
                  <Stat value={analysis.totalHours ? (analysis.totalHours / HOURS_PER_FTE).toFixed(1) : '0'} label="FTE CAPACITY" />
                  <Stat value={paybackMonths != null ? `${paybackMonths < 10 ? paybackMonths.toFixed(1) : Math.round(paybackMonths)} mo` : '—'} label={budgetN ? 'PAYBACK' : 'PAYBACK (ADD BUDGET)'} color="#a78bfa" />
                </div>
                <p style={{ fontSize: 11, color: '#475569', marginTop: 12, lineHeight: 1.6 }}>
                  Assumes {fmtNum(fteN)} FTEs at {fmtMoney(costN)} each ({HOURS_PER_FTE.toLocaleString('en-US')} hrs/yr), time split evenly across the {streamData.steps.length} steps, each opportunity removing its benchmark share of step effort, with {Math.round(adoptionN * 100)}% realized.
                </p>
              </div>

              {/* Prioritized opportunities */}
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                  <div style={sectionLabel}>TOP AI INVESTMENT PRIORITIES{painPoints.length ? ' · TAILORED TO YOUR PAIN POINTS' : ''}</div>
                  {analysis.ranked.length > 5 && (
                    <button className="vsg-noprint" onClick={() => setShowAll(v => !v)} style={{ background: 'none', border: 'none', color: '#a78bfa', fontSize: 12, cursor: 'pointer', fontFamily: 'Space Mono', whiteSpace: 'nowrap' }}>
                      {showAll ? 'Show top 5' : `Show all ${analysis.ranked.length}`}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analysis.ranked.map((x, i) => (
                    <div key={x.idx} className={!showAll && i >= 5 ? 'vsg-print-only' : undefined}
                      style={{ display: !showAll && i >= 5 ? 'none' : 'flex', alignItems: 'flex-start', gap: 14, background: activeIdx === x.idx ? 'rgba(255,255,255,0.045)' : 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '14px 18px', cursor: 'pointer' }}
                      onClick={() => selectStep(x.idx)}>
                      <div style={{ minWidth: 24, height: 24, borderRadius: '50%', background: `${roiColor[x.opp.roi]}20`, border: `1px solid ${roiColor[x.opp.roi]}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: roiColor[x.opp.roi], fontFamily: 'Space Mono', flexShrink: 0 }}>{x.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{x.opp.label}</span>
                          <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'Space Mono' }}>↳ {x.stepName}</span>
                          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Pill color={QUADRANT_COLOR[x.quadrant]}>{x.quadrant.toUpperCase()}</Pill>
                            <span style={{ fontSize: 10, color: roiColor[x.opp.roi], fontFamily: 'Space Mono', fontWeight: 700 }}>★ {x.opp.roi.toUpperCase()} ROI</span>
                          </span>
                        </div>
                        <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>{x.opp.impact}</p>
                        <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap', fontSize: 11, fontFamily: 'Space Mono', color: '#475569' }}>
                          <span>Effort: <span style={{ color: effortColor[x.opp.effort] }}>{x.opp.effort}</span></span>
                          <span>≈ <span style={{ color: '#94a3b8' }}>{fmtMoney(x.dollars)}</span>/yr</span>
                          {x.matched.length > 0 && <span style={{ color: '#a78bfa' }}>✓ {x.matched.join(' · ')}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="vsg-noprint" style={{ marginTop: 32, padding: '24px', background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 16, textAlign: 'center' }}>
                <p style={{ color: '#94a3b8', fontSize: 14, marginBottom: 16 }}>Want a custom AI roadmap for your organization? Let's connect.</p>
                <a href="https://www.linkedin.com/in/shyna/" target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-block', background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff', textDecoration: 'none', padding: '12px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14 }}>
                  Request a Custom Analysis →
                </a>
              </div>
              <p className="vsg-print-only" style={{ display: 'none', marginTop: 20, fontSize: 11, color: '#475569' }}>Generated with the AI Value Stream Generator · builditai.co</p>
            </div>
          )}
        </div>

        {toast && (
          <div role="status" style={{ position: 'sticky', bottom: 16, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ background: '#1e1b4b', border: '1px solid #a78bfa', color: '#e0e7ff', fontSize: 13, padding: '8px 16px', borderRadius: 99 }}>{toast}</span>
          </div>
        )}
      </div>

      <style>{`
        .vsg-flow::-webkit-scrollbar { height: 6px; }
        .vsg-flow::-webkit-scrollbar-thumb { background: rgba(167,139,250,0.3); border-radius: 99px; }
        .vsg-flow::-webkit-scrollbar-track { background: transparent; }
        .vsg-modal input[type=number]::-webkit-inner-spin-button { opacity: 0.4; }
        @media (max-width: 720px) {
          .vsg-stats, .vsg-roi-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
        }
        @media (max-width: 600px) {
          .vsg-overlay { padding: 8px !important; align-items: stretch !important; }
          .vsg-modal { max-height: none !important; height: 100% !important; border-radius: 16px !important; }
          .vsg-head { padding: 20px 20px 16px !important; }
          .vsg-head h2 { font-size: 19px !important; }
          .vsg-body { padding: 20px !important; }
          .vsg-size-grid, .vsg-two-col { grid-template-columns: 1fr !important; }
          .vsg-roi { padding: 16px !important; }
          .vsg-matrix { grid-template-columns: 18px repeat(3, minmax(0,1fr)) !important; gap: 4px !important; }
          .vsg-matrix button { font-size: 10px !important; padding: 3px 5px !important; }
          .vsg-actions { width: 100%; }
          .vsg-actions button { flex: 1 1 auto; }
        }
        @media print {
          @page { margin: 12mm; }
          #root > div > *:not(.vsg-overlay) { display: none !important; }
          body * { visibility: hidden !important; }
          .vsg-print-root, .vsg-print-root * { visibility: visible !important; }
          .vsg-overlay { position: static !important; background: none !important; backdrop-filter: none !important; padding: 0 !important; display: block !important; }
          .vsg-print-root { position: absolute !important; left: 0; top: 0; width: 100% !important; max-width: none !important; max-height: none !important; overflow: visible !important; border: none !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .vsg-noprint { display: none !important; }
          .vsg-print-only { display: flex !important; }
          .vsg-flow { flex-wrap: wrap !important; overflow: visible !important; row-gap: 10px; }
        }
      `}</style>
    </div>
  );
}
